import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertOctagon, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error(
      'TrafficForge ErrorBoundary capturou uma falha:',
      error,
      errorInfo
    );
    this.setState({ errorInfo });
  }

  public handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
    });
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  public toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="my-4 flex min-h-72 w-full items-center justify-center rounded-2xl border border-rose-500/30 bg-slate-900/80 p-6 shadow-xl shadow-black/40">
          <div className="w-full max-w-md space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl border border-rose-500/20 bg-rose-500/10 text-rose-400 shadow-inner">
              <AlertOctagon className="h-6 w-6" />
            </div>

            <div>
              <h3 className="text-lg font-bold text-slate-100">
                {this.props.fallbackTitle || 'Algo deu errado neste componente'}
              </h3>
              <p className="mt-1 text-xs leading-relaxed text-slate-400">
                {this.props.fallbackMessage ||
                  'Uma exceção inesperada ocorreu ao renderizar esta seção da interface.'}
              </p>
            </div>

            <div className="flex items-center justify-center gap-3 pt-1">
              <button
                type="button"
                onClick={this.handleReset}
                className="inline-flex cursor-pointer items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-xs font-semibold text-white shadow-md shadow-rose-950/50 transition-all hover:bg-rose-500 active:scale-95"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Tentar novamente
              </button>

              {this.state.error && (
                <button
                  type="button"
                  onClick={this.toggleDetails}
                  className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-xs font-medium text-slate-400 transition-colors hover:bg-slate-700 hover:text-slate-200"
                >
                  {this.state.showDetails ? 'Ocultar detalhes' : 'Ver detalhes'}
                  {this.state.showDetails ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>

            {this.state.showDetails && this.state.error && (
              <div className="mt-4 max-h-48 overflow-x-auto overflow-y-auto rounded-lg border border-slate-800 bg-slate-950/80 p-3 text-left font-mono text-micro text-rose-300">
                <p className="mb-1 font-bold">{this.state.error.toString()}</p>
                {this.state.errorInfo && (
                  <pre className="whitespace-pre-wrap text-slate-400">
                    {this.state.errorInfo.componentStack}
                  </pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
